import {HistoryDownloadFormProps} from '@di-shared/features/history/download/HistoryDownloadForm/index';
import React, {Suspense, useState} from 'react';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import {addDate, endDateOfMonth, isSameYearAndMonth, startDateOfMonth, subDate} from '@shared/dateUtils';
import {ccWebviewUtils} from '@shared/cci/features/webviewUtils';
import {ROUTE_PATHS} from '@di-shared/routes';
import usePendingHandler from '@shared/usePendingHandler';
import {diApi} from '@di-shared/api/di';
import {getYYYYMMddFormat} from '@di-shared/api/di/utils';
import {WebviewUtils} from '@shared/webviewUtils';
import Header from '@di-shared/features/common/Header';
import HeaderBackButton from '@di-shared/features/common/HeaderBackButton';
import clsx from 'clsx';
import {emitEventLog} from '@di-shared/features/analytics';
import HistoryLock from '@di-shared/features/history/setting/HistoryLock';
import {ErrorBoundary} from 'react-error-boundary';
import useDashedDatetimeFormatter from '@di-shared/utils/useDashedDatetimeFormatter';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';

export const PERIOD = {
  SELECT_MONTH: 12,
  RECENT_30_DAYS: 30,
  RECENT_90_DAYS: 90,
};

enum PeriodType {
  SELECT_MONTH,
  RECENT_30_DAYS,
  RECENT_90_DAYS,
  CUSTOM_DAYS,
}

export default function ({vin}: HistoryDownloadFormProps) {
  // TBD 다운로드 기능
  return (
    <ErrorBoundary fallback={<FallbackError />}>
      <Suspense fallback={<FallbackLoading />}>
        <HistoryLock
          vin={vin}
          fallbackLoading={<FallbackLoading />}
          fallbackError={<FallbackError />}
        >
          <Inner vin={vin} />
        </HistoryLock>
      </Suspense>
    </ErrorBoundary>
  );
}

function Inner({vin}: HistoryDownloadFormProps) {
  const today = new Date();
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const DashedDatetimeFormatter = useDashedDatetimeFormatter();
  const [form, setForm] = useState<{
    period: PeriodType | null;
    startDate: Date | null;
    endDate: Date | null;
  }>(updatePeriod(PeriodType.RECENT_30_DAYS));

  function updatePeriod(period: PeriodType) {
    switch (period) {
      case PeriodType.RECENT_30_DAYS:
        return {
          period,
          startDate: subDate(today, {days: PERIOD.RECENT_30_DAYS - 1}),
          endDate: today,
        };
      case PeriodType.RECENT_90_DAYS:
        return {
          period,
          startDate: subDate(today, {days: PERIOD.RECENT_90_DAYS - 1}),
          endDate: today,
        };
      case PeriodType.CUSTOM_DAYS:
        return {
          period,
          startDate: null,
          endDate: null,
        };
      default:
        return {
          period: null,
          startDate: null,
          endDate: null,
        };
    }
  }

  async function openSelectMonthBottomSheet() {
    const months = Array.from({length: PERIOD.SELECT_MONTH}).map((_, index) => subDate(today, {months: index}));
    const date = await ccWebviewUtils.openBottomSheetWebViewUrl<Date>(ROUTE_PATHS.BOTTOM_SHEET_HISTORY_SELECT_MONTH.getFullPath(), {
      months,
      selectedDate: form.period === PeriodType.SELECT_MONTH ? form.startDate : undefined,
    });

    if (!date) {
      return;
    }

    setForm({
      period: PeriodType.SELECT_MONTH,
      startDate: startDateOfMonth(date),
      endDate: isSameYearAndMonth(date, today) ? today : endDateOfMonth(date),
    });
  }

  async function openStartDatePicker() {
    const date = await ccWebviewUtils.openBottomSheetWebViewUrl<Date | null>(
      ROUTE_PATHS.BOTTOM_SHEET_HISTORY_DOWNLOAD_DATEPICKER.getFullPath(),
      {
        selectedDate: form.startDate,
        minDate: form.endDate
          ? subDate(form.endDate, {days: PERIOD.RECENT_90_DAYS - 1})
          : startDateOfMonth(subDate(today, {months: PERIOD.SELECT_MONTH - 1})),
        maxDate: form.endDate ? form.endDate : today,
      }
    );

    if (!date) {
      return;
    }

    setForm((prev) => ({
      period: prev.period,
      startDate: date,
      endDate: prev.endDate,
    }));
  }

  async function openEndDatePicker() {
    const date = await ccWebviewUtils.openBottomSheetWebViewUrl<Date | null>(
      ROUTE_PATHS.BOTTOM_SHEET_HISTORY_DOWNLOAD_DATEPICKER.getFullPath(),
      {
        selectedDate: form.endDate,
        minDate: form.startDate ? form.startDate : startDateOfMonth(subDate(today, {months: PERIOD.SELECT_MONTH - 1})),
        maxDate:
          form.startDate && form.startDate < subDate(today, {days: PERIOD.RECENT_90_DAYS - 1})
            ? addDate(form.startDate, {days: PERIOD.RECENT_90_DAYS - 1})
            : today,
      }
    );

    if (!date) {
      return;
    }

    setForm((prev) => ({
      period: prev.period,
      startDate: prev.startDate,
      endDate: date,
    }));
  }

  const [downloadExcel] = usePendingHandler(async () => {
    if (!form.startDate || !form.endDate) {
      return;
    }
    const {token} = await diApi.driveHistoryExcel.driveHistoryExcelDownloadToken({
      vin,
      startDate: getYYYYMMddFormat(form.startDate),
      endDate: getYYYYMMddFormat(form.endDate),
    });

    WebviewUtils.downloadUrl({
      fileName:
        `driving_${DashedDatetimeFormatter.format(form.startDate, 'date')}_${DashedDatetimeFormatter.format(form.endDate, 'date')}.xlsx`.replaceAll(
          '-',
          ''
        ),
      url: `${import.meta.env.VITE_DI_KR_API_URL}/drive-history/excel/file?token=${token}`,
      isOpenShare: true,
      mimeType: 'application/vnd.ms-excel',
      messages: {
        complete: t('T10170'),
        failure: t('T10171'),
      },
    });
  });

  return (
    <div className="wrap">
      <Header left={<HeaderBackButton />}>
        <h1 className="headline_xxsmall nav_center_title">
          <span className="blind">{t('T10089')}</span>
        </h1>
      </Header>
      <main className="content page_setting">
        <section className="section">
          <div className="section_heading">
            <h2 className="section_heading_title">{t('T10089')}</h2>
            <p className="section_heading_desc">{t('T10095')}</p>
          </div>
          <div className="setting_box">
            <h3 className="title_xxsmall">{t('T10096')}</h3>
            <button
              className="btn_select"
              type="button"
              onClick={openSelectMonthBottomSheet}
            >
              {form.period !== PeriodType.SELECT_MONTH ? (
                <span className="select_placeholder">{t('T10192')}</span>
              ) : (
                <span className="select_value">{CldrDatetimeFormatter.format(form.startDate ? form.startDate : today, 'yMMM')}</span>
              )}
              <span
                className="ic ic_arrow_down_16px"
                aria-hidden="true"
              />
            </button>
          </div>
          <div className="setting_box">
            <h3 className="title_xxsmall">{t('T10097')}</h3>
            <div className="area_period">
              <ul className="list_period">
                <li className="list_period_item">
                  <button
                    className={clsx('btn btn_filled_tonal', {'is-selected': form.period === PeriodType.RECENT_30_DAYS})}
                    type="button"
                    onClick={() => setForm(updatePeriod(PeriodType.RECENT_30_DAYS))}
                  >
                    {t('T30082_$days', {days: PERIOD.RECENT_30_DAYS})}
                  </button>
                </li>
                <li className="list_period_item">
                  <button
                    className={clsx('btn btn_filled_tonal', {'is-selected': form.period === PeriodType.RECENT_90_DAYS})}
                    type="button"
                    onClick={() => setForm(updatePeriod(PeriodType.RECENT_90_DAYS))}
                  >
                    {t('T30082_$days', {days: PERIOD.RECENT_90_DAYS})}
                  </button>
                </li>
                <li className="list_period_item">
                  <button
                    className={clsx('btn btn_filled_tonal', {'is-selected': form.period === PeriodType.CUSTOM_DAYS})}
                    type="button"
                    onClick={() => setForm(updatePeriod(PeriodType.CUSTOM_DAYS))}
                  >
                    {t('T10165')}
                  </button>
                </li>
              </ul>
              <div className="select_date_box">
                <div className="btn_box">
                  <button
                    className={clsx('btn btn_full btn_outline btn_date', {
                      type_placeholder: form.period !== PeriodType.CUSTOM_DAYS,
                    })}
                    type="button"
                    disabled={form.period !== PeriodType.CUSTOM_DAYS}
                    onClick={() => openStartDatePicker()}
                  >
                    <span className="body_large_r text_date">
                      {form.startDate ? CldrDatetimeFormatter.format(form.startDate, 'medium') : t('T10166')}
                    </span>
                    <span className="ic ic_calendar_20px" />
                  </button>
                  <span
                    className="body_large_b tilde"
                    role="none"
                  >
                    -
                  </span>
                  <button
                    className={clsx('btn btn_full btn_outline btn_date', {
                      type_placeholder: form.period !== PeriodType.CUSTOM_DAYS,
                    })}
                    type="button"
                    disabled={form.period !== PeriodType.CUSTOM_DAYS}
                    onClick={() => openEndDatePicker()}
                  >
                    <span className="body_large_r text_date">
                      {form.endDate ? CldrDatetimeFormatter.format(form.endDate, 'medium') : t('T10167')}
                    </span>
                    <span className="ic ic_calendar_20px" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <div className="floating_action_area">
        <button
          type="button"
          className="btn btn_large btn_filled btn_full"
          disabled={!form.startDate || !form.endDate}
          onClick={() => {
            emitEventLog({
              event: 'driving_records_download',
              form_step: 'download',
              click_text: t('T10099'),
              click_location: 'body',
            });

            downloadExcel();
          }}
        >
          {t('T10099')}
        </button>
      </div>
    </div>
  );
}

function FallbackLoading() {
  const {t} = useInferableTranslation();

  return (
    <div className="wrap">
      <Header left={<HeaderBackButton />}>
        <h1 className="headline_xxsmall nav_center_title">
          <span className="blind">{t('T10089')}</span>
        </h1>
      </Header>
      <main className="content page_setting">
        <section className="section">
          <div className="section_heading">
            <h2
              className="section_heading_title skeleton"
              style={{width: '160px'}}
            >
              {t('T10089')}
            </h2>
            <p
              className="section_heading_desc skeleton"
              style={{width: '200px'}}
            >
              {t('T10095')}
            </p>
          </div>
          <div className="setting_box skeleton">
            <h3 className="title_xxsmall">{t('T10096')}</h3>
            <button
              className="btn_select"
              type="button"
            >
              <span className="select_placeholder">{t('T10192')}</span>
              <span
                className="ic ic_arrow_down_16px"
                aria-hidden="true"
              />
            </button>
          </div>
          <div className="setting_box skeleton">
            <h3 className="title_xxsmall">{t('T10097')}</h3>
            <div className="area_period">
              <ul className="list_period">
                <li className="list_period_item">
                  <button
                    className="btn btn_filled_tonal"
                    type="button"
                  >
                    {t('T30082_$days', {days: PERIOD.RECENT_30_DAYS})}
                  </button>
                </li>
                <li className="list_period_item">
                  <button
                    className="btn btn_filled_tonal"
                    type="button"
                  >
                    {t('T30082_$days', {days: PERIOD.RECENT_90_DAYS})}
                  </button>
                </li>
                <li className="list_period_item">
                  <button
                    className="btn btn_filled_tonal"
                    type="button"
                  >
                    {t('T10165')}
                  </button>
                </li>
              </ul>
              <div className="select_date_box">
                <div className="btn_box">
                  <button
                    className="btn btn_full btn_outline btn_date"
                    type="button"
                  >
                    <span className="body_large_r text_date">{t('T10166')}</span>
                    <span className="ic ic_calendar_20px" />
                  </button>
                  <span
                    className="body_large_b tilde"
                    role="none"
                  >
                    -
                  </span>
                  <button
                    className="btn btn_full btn_outline btn_date"
                    type="button"
                  >
                    <span className="body_large_r text_date">{t('T10167')}</span>
                    <span className="ic ic_calendar_20px" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <div className="floating_action_area">
        <button
          type="button"
          className="btn btn_large btn_filled btn_full"
        >
          {t('T10099')}
        </button>
      </div>
    </div>
  );
}

function FallbackError() {
  const {t} = useInferableTranslation();

  return (
    <div className="wrap">
      <Header left={<HeaderBackButton />}>
        <h1 className="headline_xxsmall nav_center_title">
          <span className="blind">{t('T10089')}</span>
        </h1>
      </Header>
      <main className="content page_setting">
        <section className="section">
          <div className="section_heading">
            <h2 className="section_heading_title">{t('T10089')}</h2>
            <p className="section_heading_desc">{t('T10095')}</p>
          </div>
          <div className="setting_box">
            <h3 className="title_xxsmall">{t('T10097')}</h3>
            <div className="area_period">
              <ul className="list_period">
                <li className="list_period_item">
                  <button
                    className="btn btn_filled_tonal"
                    type="button"
                  >
                    {t('T30082_$days', {days: PERIOD.RECENT_30_DAYS})}
                  </button>
                </li>
                <li className="list_period_item">
                  <button
                    className="btn btn_filled_tonal"
                    type="button"
                  >
                    {t('T30082_$days', {days: PERIOD.RECENT_90_DAYS})}
                  </button>
                </li>
                <li className="list_period_item">
                  <button
                    className="btn btn_filled_tonal"
                    type="button"
                  >
                    {t('T10165')}
                  </button>
                </li>
              </ul>
              <div className="select_date_box">
                <div className="btn_box">
                  <button
                    className="btn btn_full btn_outline btn_date"
                    type="button"
                  >
                    <span className="body_large_r text_date">{t('T10166')}</span>
                    <span className="ic ic_calendar_20px" />
                  </button>
                  <span
                    className="body_large_b tilde"
                    role="none"
                  >
                    -
                  </span>
                  <button
                    className="btn btn_full btn_outline btn_date"
                    type="button"
                  >
                    <span className="body_large_r text_date">{t('T10167')}</span>
                    <span className="ic ic_calendar_20px" />
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="setting_box">
            <h3 className="title_xxsmall">{t('T10096')}</h3>
            <button
              className="btn_select"
              type="button"
            >
              <span className="select_placeholder">{t('T10192')}</span>
              <span
                className="ic ic_arrow_down_16px"
                aria-hidden="true"
              />
            </button>
          </div>
        </section>
      </main>
      <div className="floating_action_area">
        <button
          type="button"
          className="btn btn_large btn_filled btn_full"
        >
          {t('T10099')}
        </button>
      </div>
    </div>
  );
}
